// React & Third-Party Imports
import {
  useState,
  useMemo,
  useCallback,
  Suspense,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { Card, Col, Container, Row } from "reactstrap";
import { skipToken } from "@reduxjs/toolkit/query";

// Hooks & Redux Imports
import { useAppDispatch, useAppSelector } from "../../../ReduxToolkit/Hooks";
import { useModalSelector } from "../../../hooks/useModalSelector";
import { useEmployeeProfile } from "../../../hooks/useEmployeeProfile";

// Redux Slices & Actions
import { setSelectedFinancialYear } from "../../../ReduxToolkit/Slice/FinancialYearSlice";
import { closeModal } from "../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";

// Selectors
import { selectEmployeeSecondaryTabActive } from "../../employeeManagement/redux/selector/EmployeeManagementSelector";

// API Hooks
import { useListAttendanceQuery } from "../../../ReduxToolkit/api/attendance";
import EmployeeAttendanceSummaryTab from "../../employeeManagement/components/TabContent/EmployeeAttendanceSummaryTab";
import { Edit3 } from "react-feather";
import AttendanceTimeBadge from "./locationTimeCell/AttendanceTimeBadge";
import { openModal } from "../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { fetchAssginedManagerToEmployee } from "../../hrZone/Users/redux/slice/AssignedManagerToEmployeeSlice";
import { IProfileLeaveAttendancePopup } from "../../../Types/UserProfileDetails";

// UI Components
import Loader from "../../../Layout/Loader/Loader";
import AttendanceHeader from "./header/AttendanceHeader";
import AttendanceDatePicker from "./datePicker/AttendanceDatePicker";
import AttendanceTable from "./table/AttendanceTable";
import AttendanceDetailsCard from "./card/AttendanceDetailsCard";
import AttendanceCorrectionModal from "./modal/AttendanceCorrectionModal";

// Utils, Constants & Types
import {
  attendanceCorrectionModal,
  EndDate,
  long,
  StartDate,
  Submit,
} from "../../../utils/Constant";
import { dateConversion, formattedYMDDate } from "../../../utils/dateUtils";
import { logErrors } from "../../../utils/logErrors";
import { IAttendanceDetailsResponse } from "../types";
import "../attendance.scss";
import { showApiResultAlert } from "../../../utils/AlertManager/showApiAlert";
import { selectFinancialYearData } from "../redux/selector/attendanceSelector";
import { alertManager } from "../../../utils/AlertManager";

const getAttendanceCycleForCalendarMonth = (today = new Date()) => {
  const year = today.getFullYear();
  const month = today.getMonth(); // 0 = Jan ... 11 = Dec
  const date = today.getDate();

  let start, end;

  if (date <= 7) {
    // Before or on 7th → cycle is from 25th of two months ago till today
    const startMonth = month - 1; // previous month
    const startYear = startMonth < 0 ? year - 1 : year;
    const adjustedStartMonth = (startMonth + 12) % 12;

    start = new Date(startYear, adjustedStartMonth, 25);
    end = new Date(today); // till today
  } else {
    // After 7th → cycle is from 25th of last month till today
    const startMonth = month; // current month - 1
    const startYear = startMonth < 1 ? year - 1 : year;
    const adjustedStartMonth = (startMonth - 1 + 12) % 12;

    start = new Date(startYear, adjustedStartMonth, 25);
    end = new Date(today);
  }

  return { start, end };
};

const getAttendanceDateKey = (date: string | number) => {
  const value = String(date);
  const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;

  return formattedYMDDate(value);
};

const isCorrectionRequestAlreadySent = (
  attendance?: IAttendanceDetailsResponse | null,
) =>
  attendance?.remark_all?.toLowerCase().replace(/[\s_-]+/g, "") ===
  "correctionrequestsent";

// Component
const ViewAttendance = () => {
  // ---------------------- React Router ----------------------
  const location = useLocation();

  // ---------------------- Redux ----------------------
  const dispatch = useAppDispatch();
  const selectedFinancialYear = useAppSelector(selectFinancialYearData);
  const secondaryTabActive = useAppSelector(selectEmployeeSecondaryTabActive);

  // ---------------------- Custom Hooks ----------------------
  const { selectedId, modalType } = useModalSelector();
  const {
    employeeProfileDetails,
    loggedInEmployeeRole,
    employeeRole,
    loggedInEmployee,
  } = useEmployeeProfile();

  // ---------------------- Derived Constants ----------------------
  const currentDate = new Date();
  const { start: cycleStart, end: cycleEnd } =
    getAttendanceCycleForCalendarMonth(currentDate);

  const employeeId = Number(employeeProfileDetails?.jobInformation?.userId);
  const financialYear = selectedFinancialYear?.split("-")[0];

  // ---------------------- Local State ----------------------
  const [startDate, setStartDate] = useState(formattedYMDDate(cycleStart));
  const [endDate, setEndDate] = useState(formattedYMDDate(cycleEnd));
  const [searchParams, setSearchParams] = useState({
    startDate: formattedYMDDate(cycleStart),
    endDate: formattedYMDDate(cycleEnd),
  });
  const correctionRequestAlertDateRef = useRef<string | number | null>(null);
  const [pageSummary, setPageSummary] =
    useState<IProfileLeaveAttendancePopup>();
  const handleSummaryRange = useCallback(
    (range: { startDate: string; endDate: string }) => {
      setPageSummary(undefined);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      setSearchParams(range);
      const year =
        Number(range.endDate.slice(0, 4)) -
        (Number(range.endDate.slice(5, 7)) < 10 ? 1 : 0);
      dispatch(
        setSelectedFinancialYear({
          module: "attendance",
          year: `${year}-${year + 1}`,
        }),
      );
    },
    [dispatch],
  );

  // ---------------------- Derived Flags ----------------------
  const isAttendancePath =
    location.pathname === "/employee-zone/view-attendance" &&
    cycleStart &&
    cycleEnd &&
    financialYear &&
    employeeId;

  const isEmployeeManagementPath =
    cycleStart &&
    cycleEnd &&
    financialYear &&
    employeeId &&
    Number(secondaryTabActive) === 401;

  const isAttendancePage =
    location.pathname === "/employee-zone/view-attendance" ||
    location.pathname.includes("/user-management/user");
  const shouldFetch = !isAttendancePage && isEmployeeManagementPath;

  // ---------------------- API Calls ----------------------
  const {
    data: legacyAttendanceDetailsData,
    isLoading: isAttendanceDetailsLoading,
    isFetching: isAttendanceDetailsFetching,
    isError: isAttendanceDetailsError,
    error: attendanceDetailsError,
  } = useListAttendanceQuery(
    shouldFetch
      ? {
          activeYear: Number(financialYear),
          startDate: formattedYMDDate(searchParams.startDate),
          endDate: formattedYMDDate(searchParams.endDate),
          userId: String(employeeId),
        }
      : skipToken,
  );

  const attendanceDetailsData = useMemo(
    () =>
      isAttendancePage
        ? {
            data: (pageSummary?.working_hours.daily_breakdown ?? []).map(
              (day): IAttendanceDetailsResponse => ({
                emp_name: day.emp_name ?? loggedInEmployee?.name ?? null,
                date: day.date,
                in_time: day.in_time,
                out_time: day.out_time,
                in_location: day.in_location ?? null,
                out_location: day.out_location ?? null,
                in_latitude: day.in_latitude ?? null,
                in_longitude: day.in_longitude ?? null,
                out_latitude: day.out_latitude ?? null,
                out_longitude: day.out_longitude ?? null,
                username: day.username ?? loggedInEmployee?.name ?? "",
                user_id: String(day.user_id ?? employeeId),
                remark_all: day.remark_all ?? day.status,
                working_hours: String(day.logged_hours),
                expected_hours: day.expected_hours,
                logged_hours: day.logged_hours,
                deficit_hours: day.deficit_hours,
                editable: day.editable === true,
              }),
            ),
          }
        : legacyAttendanceDetailsData,
    [
      isAttendancePage,
      pageSummary,
      legacyAttendanceDetailsData,
      loggedInEmployee?.name,
      employeeId,
    ],
  );

  // ---------------------- Handlers ----------------------
  const handleChangeStartDate = useCallback(
    (date: Date) => setStartDate(formattedYMDDate(date)),
    [],
  );

  const handleChangeEndDate = useCallback(
    (date: Date) => setEndDate(formattedYMDDate(date)),
    [],
  );

  const handleSubmitAttendanceList = useCallback(
    (start: string, end: string) => {
      if (start && end && financialYear) {
        setSearchParams({
          startDate: formattedYMDDate(start),
          endDate: formattedYMDDate(end),
        });
      }
    },
    [financialYear],
  );

  const handleSetSelectedYear = useCallback(
    (year: string) => {
      dispatch(setSelectedFinancialYear({ module: "attendance", year }));
    },
    [dispatch],
  );

  // ---------------------- Derived Attendance Match ----------------------
  const findMatchedAttendance =
    useMemo<IAttendanceDetailsResponse | null>(() => {
      if (!selectedId || !attendanceDetailsData?.data) return null;

      const selectedDateKey = getAttendanceDateKey(selectedId);
      const match = attendanceDetailsData.data.find(
        (attendance) =>
          getAttendanceDateKey(attendance.date) === selectedDateKey,
      );

      if (!match) {
        logErrors(`No attendance found for date: ${selectedId}`);
        return null;
      }

      return match;
    }, [attendanceDetailsData, selectedId]);

  const hasCorrectionRequestAlreadySent = useMemo(
    () => isCorrectionRequestAlreadySent(findMatchedAttendance),
    [findMatchedAttendance],
  );

  useEffect(() => {
    if (isAttendanceDetailsError) {
      showApiResultAlert(attendanceDetailsError, {
        title: `Hey ${loggedInEmployee?.name}!`,
      });
    }
  }, [isAttendanceDetailsError]);

  useEffect(() => {
    if (
      modalType !== attendanceCorrectionModal ||
      !selectedId ||
      !findMatchedAttendance ||
      !hasCorrectionRequestAlreadySent ||
      correctionRequestAlertDateRef.current === selectedId
    ) {
      return;
    }

    correctionRequestAlertDateRef.current = selectedId;
    dispatch(closeModal());
    alertManager.showAlert({
      type: "warning",
      title: "Correction Request Already Sent",
      message: `Correction request already sent for ${dateConversion(
        findMatchedAttendance.date,
        true,
      )}. Please wait for approval.`,
      source: "attendanceCorrectionModal",
    });
  }, [
    dispatch,
    findMatchedAttendance,
    hasCorrectionRequestAlreadySent,
    modalType,
    selectedId,
  ]);

  // ---------------------- Render ----------------------
  const attendanceTable = (
    <Row>
      <AttendanceTable
        dispatch={dispatch}
        attendanceLoading={
          isAttendanceDetailsLoading || isAttendanceDetailsFetching
        }
        employeeAttendanceDetails={attendanceDetailsData ?? { data: [] }}
      />
    </Row>
  );
  return (
    <Container fluid>
      <Row>
        <Col xs="12">
          {isAttendancePage ? (
            <div
              className={`attendance-view-summary ${location.pathname.includes("/user-management/user") ? "mt-0" : "mt-4"}`}
            >
              <EmployeeAttendanceSummaryTab
                selfService
                onRangeChange={handleSummaryRange}
                onSummaryChange={setPageSummary}
                renderDayTime={(date, kind, time) => {
                  const row = attendanceDetailsData?.data?.find(
                    (entry) => getAttendanceDateKey(entry.date) === date,
                  );
                  const locationText =
                    kind === "in_time" ? row?.in_location : row?.out_location;
                  return (
                    <AttendanceTimeBadge
                      time={time}
                      kind={kind}
                      location={locationText}
                    />
                  );
                }}
                renderDayAction={(date) => {
                  const row = attendanceDetailsData?.data?.find(
                    (entry) => getAttendanceDateKey(entry.date) === date,
                  );
                  if (!row?.editable || isAttendanceDetailsFetching)
                    return null;
                  return (
                    <button
                      type="button"
                      className="attendance-correction-button"
                      title="Request correction"
                      aria-label={`Request correction for ${date}`}
                      onClick={() => {
                        dispatch(
                          fetchAssginedManagerToEmployee({
                            id: Number(row.user_id),
                          }),
                        );
                        dispatch(
                          openModal({
                            modalType: attendanceCorrectionModal,
                            selectedId: row.date,
                          }),
                        );
                      }}
                    >
                      <Edit3 size={13} />
                    </button>
                  );
                }}
              />
            </div>
          ) : (
            <>
              <Card
                className={`mt-${
                  location.pathname.includes("/user-management/user")
                    ? "0"
                    : "4"
                } mb-2 px-4 pt-3 pb-4`}
              >
                <AttendanceHeader
                  employeeRole={employeeRole}
                  loggedInEmployeeRole={loggedInEmployeeRole}
                  selectedFinancialYear={selectedFinancialYear!}
                  handleSetSelectedYear={handleSetSelectedYear}
                />

                <div
                  className={
                    isAttendancePage ? "attendance-view-filters" : undefined
                  }
                >
                  <AttendanceDatePicker
                    autoApply={isAttendancePage}
                    startLabel={StartDate}
                    endLabel={EndDate}
                    submitLabel={Submit}
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={handleChangeStartDate}
                    onEndDateChange={handleChangeEndDate}
                    onSubmit={handleSubmitAttendanceList}
                  />
                </div>

                {attendanceTable}
              </Card>
            </>
          )}
        </Col>

        {/* Mobile View - Details Cards */}
        {!isAttendancePage && (
          <div className="d-block d-lg-none">
            <Col xs="12">
              <Row>
                {attendanceDetailsData?.data
                  ?.slice()
                  ?.reverse()
                  ?.map((attendance, index) => {
                    const date = new Date(attendance.date);
                    const dayName = date.toLocaleDateString("en-US", {
                      weekday: long,
                    });

                    return (
                      <AttendanceDetailsCard
                        key={index}
                        index={index}
                        dayName={dayName}
                        dispatch={dispatch}
                        employeeAttendanceDetails={attendance}
                        attendanceLoading={isAttendanceDetailsLoading}
                      />
                    );
                  })}
              </Row>
            </Col>
          </div>
        )}
      </Row>

      {/* Attendance Correction Modal */}
      <Suspense fallback={<Loader />}>
        {modalType === attendanceCorrectionModal &&
          selectedId &&
          findMatchedAttendance &&
          !hasCorrectionRequestAlreadySent &&
          financialYear && (
            <AttendanceCorrectionModal
              endDate={endDate}
              dispatch={dispatch}
              startDate={startDate}
              financialYear={financialYear}
              findMatchedAttendance={findMatchedAttendance}
            />
          )}
      </Suspense>
    </Container>
  );
};

export default ViewAttendance;
